import { FC, useEffect, useState } from "react";
import { Grid, Form } from "antd";
import type { FormInstance } from "antd";
import { useTranslation } from "react-i18next";
import { withConditionalComponent } from "src/HOC/WithMobileComponent";
import { MOBILE_BREAKPOINT_KEY } from "src/constants";
import type { PassengerType } from "src/Modules/Flights/constants/JourneyDetails";
import PassengerTabsWeb from "./layouts/PassengerTabs.web";
import PassengerTabsMob from "./layouts/PassengerTabs.mob";

export interface PassengerTabItem {
  value: string;
  name: string;
  typeLabel: string;
  groupKey?: string | number;
  groupLabel?: string;
  isLead: boolean;
  isComplete: boolean;
  hasError: boolean;
}

export interface PassengerTabsLabels {
  title: string;
  lead: string;
  completed: (count: number, total: number) => string;
  progress: (current: number, total: number) => string;
}

export interface PassengerTabsRenderProps {
  items: PassengerTabItem[];
  value: string;
  onChange: (value: string) => void;
  labels: PassengerTabsLabels;
}

const PassengerTabsConditional = withConditionalComponent(PassengerTabsWeb, PassengerTabsMob);

const belongsToPassenger = (name: (string | number)[], index: number) => name[0] === "passengers" && name[1] === index;

function usePassengersCompletion(form: FormInstance, count: number, values: unknown) {
  const [completion, setCompletion] = useState<boolean[]>([]);

  useEffect(() => {
    let cancelled = false;
    const allNames = form.getFieldsError().map((field) => field.name);
    Promise.all(
      Array.from({ length: count }, (_, index) => {
        const names = allNames.filter((name) => belongsToPassenger(name, index));
        if (!names.length) return Promise.resolve(false);
        return form.validateFields(names, { validateOnly: true }).then(
          () => true,
          () => false,
        );
      }),
    ).then((result) => {
      if (!cancelled) setCompletion(result);
    });
    return () => {
      cancelled = true;
    };
  }, [values, count]);

  return completion;
}

interface PassengerTabsProps {
  fields: { key: number; name: number }[];
  passengers: PassengerType[];
  activePassengerState: [number, (index: number) => void];
  form: FormInstance;
}

const PassengerTabs: FC<PassengerTabsProps> = ({ fields, passengers, activePassengerState, form }) => {
  const { t } = useTranslation();
  const [activePassenger, setActivePassenger] = activePassengerState;
  const isLargeScreen = !!Grid.useBreakpoint()[MOBILE_BREAKPOINT_KEY];
  const watchedPassengers: PassengerType[] | undefined = Form.useWatch(["passengers"], { form, preserve: true });
  const completion = usePassengersCompletion(form, fields.length, watchedPassengers);
  const erroredIndexes = new Set(
    form
      .getFieldsError()
      .filter((field) => field.errors.length && field.name[0] === "passengers")
      .map((field) => field.name[1]),
  );

  const items: PassengerTabItem[] = fields.map((_, index) => {
    const passenger = watchedPassengers?.[index] ?? passengers[index];
    const hasError = erroredIndexes.has(index);
    return {
      value: String(index),
      name: passenger?.basicData?.firstName?.trim() || t("Flights.details.passengers.passengerNumber", { number: index + 1 }),
      typeLabel: t(`Flights.details.passengerType.${passengers[index]?.type?.toLowerCase()}`),
      isLead: !!passenger?.basicData?.isLeadPax,
      hasError,
      isComplete: !hasError && !!completion[index],
    };
  });

  const renderProps: PassengerTabsRenderProps = {
    items,
    value: String(activePassenger),
    onChange: (val) => setActivePassenger(Number(val)),
    labels: {
      title: t("Flights.details.summary.passengerDetails"),
      lead: t("Flights.details.passengerCard.primaryContact"),
      completed: (count, total) => t("Flights.details.passengers.passengersCompleted", { count, total }),
      progress: (current, total) => t("Flights.details.passengers.passengerProgress", { current, total }),
    },
  };

  return <PassengerTabsConditional trigger={isLargeScreen} props={renderProps} />;
};

export default PassengerTabs;
